/**
 * المرحلة 1.35 (R6) — «إيصال تحصيل نقدية», drawn as the centre's paper is:
 * the logo and the rounded title box, the number in red, the two amount boxes
 * (جنيه | قرش), the dotted lines «وصل من السيد/» and «مبلغ وقدره/», then
 * «وذلك نظير/» as a table, the date and the hour, the status as a stamp, and
 * «المتسلم» with the issuer's name and title. A named discount prints under
 * its line and above the total (its name and amount, never the desk's
 * reason); a refund («مرتجع») prints under the total, with what is left.
 *
 * One component for the desk's A4 print (printArea) and the guardian's soft
 * copy (PublicReceiptPage): both pass the same data, domain/voucher.js
 * sheetOf on the desk and the server's publicVoucher on the public page.
 * Always on white paper, in colour, right to left, whatever the theme.
 */

import { forwardRef } from 'react';
import { Barcode } from '../../components/common';
import { useBranding } from '../../services/branding';
import { pickLogo } from '../../domain/logoBackdrop';

const UNIT = { once: '', day: 'يوم', hour: 'ساعة', session: 'جلسة', unit: 'قطعة' };
const RELATION = { father: 'الأب', mother: 'الأم', guardian: 'ولي الأمر', self: '', other: '' };

const pad = (n) => String(n).padStart(2, '0');
const figure = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export const VoucherSheet = forwardRef(function VoucherSheet({ sheet, qrSvg = null, className = '' }, ref) {
  const brand = useBranding();
  const s = sheet || {};
  const inst = s.institution || {};
  // Paper is a light page: the light mark, on its plate, whatever the theme.
  const sheetLogo = pickLogo({ logo: inst.logo, logoDark: inst.logoDark, backdrop: inst.logoBackdrop }, { dark: false });
  const issued = s.issuedAt ? new Date(s.issuedAt) : new Date();
  const date = `${pad(issued.getDate())}/${pad(issued.getMonth() + 1)}/${issued.getFullYear()}`;
  const time = `${pad(issued.getHours())}:${pad(issued.getMinutes())}`;
  const boxes = s.boxes || ['جنيه', 'قرش'];
  const relation = RELATION[s.payerRelation] || '';
  const refunds = Array.isArray(s.refunds) ? s.refunds : [];
  const refunded = refunds.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
  const stamp =
    s.status === 'void'
      ? { tone: 'void', text: 'ملغي' }
      : refunded > 0 && refunded >= Number(s.total || 0) - 0.005
        ? { tone: 'void', text: 'مرتجع' }
        : s.remaining > 0
        ? { tone: 'due', text: `آجل — الباقي ${figure(s.remaining)} ${boxes[0]}` }
        : { tone: 'paid', text: 'خالص' };

  return (
    <div ref={ref} className={`print-sheet vch ${className}`} dir="rtl" data-receipt-number={s.number}>
      <header className="vch-head">
        <div className="vch-org">
          {sheetLogo.src && <img src={sheetLogo.src} alt="" className={`vch-logo ${sheetLogo.plate}`} />}
          <div className="vch-org-text">
            <b className="vch-org-name">{inst.name || 'المنشأة الطبية'}</b>
            {(inst.address || inst.phone) && (
              <span className="vch-org-meta">
                {inst.address}
                {inst.address && inst.phone && ' — '}
                {inst.phone && <span dir="ltr">{inst.phone}</span>}
              </span>
            )}
          </div>
        </div>
        <div className="vch-title">{s.title || 'إيصال تحصيل نقدية'}</div>
        <div className="vch-no">
          <span className="vch-no-row">
            <span className="vch-no-mark">№</span>
            <b className="vch-no-value" dir="ltr">
              {s.number}
            </b>
          </span>
          {s.serial && <Barcode value={s.serial} height={34} module={1.2} />}
        </div>
      </header>

      <div className="vch-amount">
        <table className="vch-boxes">
          <thead>
            <tr>
              <th>{boxes[0]}</th>
              <th>{boxes[1]}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td dir="ltr">{figure(s.major)}</td>
              <td dir="ltr">{pad(s.minor || 0)}</td>
            </tr>
          </tbody>
        </table>
        <div className="vch-when">
          <span>
            التاريخ: <b dir="ltr">{date}</b>
          </span>
          <span>
            الساعة: <b dir="ltr">{time}</b>
          </span>
        </div>
      </div>

      <p className="vch-line">
        <span className="vch-label">وصل من السيد/</span>
        <span className="vch-dots">
          {s.payerName || '—'}
          {relation && <small> ({relation})</small>}
        </span>
      </p>
      <p className="vch-line">
        <span className="vch-label">{s.isMinor ? 'اسم الطفل/' : 'اسم المريض/'}</span>
        <span className="vch-dots">
          {s.patientName || '—'}
          {(s.mobile || s.mobileTail) && (
            <small className="vch-mobile">
              {' — '}
              <span dir="ltr">{s.mobile || s.mobileTail}</span>
            </small>
          )}
        </span>
      </p>
      <p className="vch-line">
        <span className="vch-label">مبلغ وقدره/</span>
        <span className="vch-dots vch-words">{s.words}</span>
      </p>

      <div className="vch-for">
        <span className="vch-label">وذلك نظير/</span>
        <table className="vch-items">
          <thead>
            <tr>
              <th>الخدمة</th>
              <th className="vch-num">الكمية</th>
              <th className="vch-num">السعر</th>
              <th className="vch-num">المبلغ</th>
            </tr>
          </thead>
          <tbody>
            {(s.items || []).map((item, i) => (
              <tr key={i}>
                <td>
                  {Array.isArray(item.path) ? item.path.join(' — ') : item.label}
                  {item.discount && (
                    <small className="vch-off">
                      {item.discount.name}
                      {item.discount.kind === 'percent' ? ` ${figure(item.discount.value)}%` : ''} −{figure(item.discount.amount)}
                    </small>
                  )}
                </td>
                <td className="vch-num">
                  {figure(item.quantity)} {UNIT[item.unit] || ''}
                </td>
                <td className="vch-num">{figure(item.unitPrice)}</td>
                <td className="vch-num">
                  {item.discount && <s className="vch-gross">{figure(item.gross)}</s>}
                  {figure(item.amount)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {s.discount && (
              <>
                <tr>
                  <td colSpan={3}>الإجمالي قبل الخصم</td>
                  <td className="vch-num">{figure(s.discount.subtotal)}</td>
                </tr>
                <tr className="vch-off-row">
                  <td colSpan={3}>
                    الخصم
                    {s.discount.invoice && (
                      <small>
                        {' '}
                        ({s.discount.invoice.name}
                        {s.discount.invoice.kind === 'percent' ? ` ${figure(s.discount.invoice.value)}%` : ''})
                      </small>
                    )}
                  </td>
                  <td className="vch-num">−{figure(s.discount.total)}</td>
                </tr>
              </>
            )}
            <tr>
              <th colSpan={3}>الإجمالي</th>
              <th className="vch-num">
                {figure(s.total)} {boxes[0]}
              </th>
            </tr>
            {refunds.map((x, i) => (
              <tr key={`r${i}`} className="vch-off-row">
                <td colSpan={3}>
                  مرتجع: {x.lines.map((l) => l.label).join('، ')}
                </td>
                <td className="vch-num">−{figure(x.amount)}</td>
              </tr>
            ))}
            {refunded > 0 && (
              <tr>
                <th colSpan={3}>الصافي بعد المرتجع</th>
                <th className="vch-num">
                  {figure(Number(s.total || 0) - refunded)} {boxes[0]}
                </th>
              </tr>
            )}
            {s.remaining > 0 && (
              <tr>
                <td colSpan={3}>المدفوع الآن — الباقي</td>
                <td className="vch-num">
                  {figure(s.paidAmount)} — {figure(s.remaining)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      <footer className="vch-foot">
        <div className={`vch-stamp vch-stamp--${stamp.tone}`}>{stamp.text}</div>
        <div className="vch-sign">
          <span className="vch-label">المتسلم/</span>
          <b className="vch-sign-name">{s.signatureName}</b>
          {s.signatureTitle && <span className="vch-sign-title">{s.signatureTitle}</span>}
        </div>
        {qrSvg && <div className="vch-qr" aria-hidden="true" dangerouslySetInnerHTML={{ __html: qrSvg }} />}
      </footer>

      <div className="vch-brand">
        {brand.product && brand.product.src && <img src={brand.product.src} alt="" className="vch-brand-mark" />}
        <span>{brand.name || 'Capsule'}</span>
      </div>
    </div>
  );
});

export default VoucherSheet;
